import React, { useEffect, useState } from 'react';
import { Check, X, Trash2, Star } from 'lucide-react';
import { adminAPI } from '../../api/admin';
import api from '../../api/axios';
import toast from 'react-hot-toast';

const ReviewsAdminPage: React.FC = () => {
  const [reviews, setReviews] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved'>('pending');

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      setIsLoading(true);
      const res = await api.get('/api/reviews/all');
      if (res.data.success) setReviews(res.data.data || []);
    } catch {
      setReviews([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = async (id: string, approve: boolean) => {
    try {
      await adminAPI.approveReview(id, approve);
      toast.success(approve ? 'Review approved' : 'Review rejected');
      fetchReviews();
    } catch {
      toast.error('Failed to update review');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this review?')) return;
    try {
      await api.delete(`/api/reviews/${id}`);
      toast.success('Review deleted');
      fetchReviews();
    } catch {
      toast.error('Failed to delete review');
    }
  };

  const filtered = reviews.filter((r) => {
    if (filter === 'pending') return !r.is_approved;
    if (filter === 'approved') return r.is_approved;
    return true;
  });

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs tracking-widest uppercase text-nude mb-1">Management</p>
        <h1 className="font-serif text-3xl text-primary">Reviews</h1>
      </div>

      {/* Filter Tabs */}
      <div className="flex space-x-1 border-b border-light">
        {(['all', 'pending', 'approved'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-6 py-3 text-xs tracking-widest uppercase transition-colors ${
              filter === f
                ? 'border-b-2 border-primary text-primary'
                : 'text-nude hover:text-primary'
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      <div className="space-y-4">
        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="skeleton h-24 rounded" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 bg-white border border-light">
            <p className="text-xs tracking-widest uppercase text-nude">
              No reviews found
            </p>
          </div>
        ) : (
          filtered.map((review) => (
            <div
              key={review.id}
              className={`bg-white border p-6 ${
                !review.is_approved ? 'border-amber-200' : 'border-light'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="flex space-x-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          size={12}
                          className={
                            star <= review.rating
                              ? 'fill-primary text-primary'
                              : 'text-nude'
                          }
                        />
                      ))}
                    </div>
                    <span className="text-xs font-medium">
                      {review.first_name} {review.last_name}
                    </span>
                    <span className="text-xs text-nude">
                      {new Date(review.created_at).toLocaleDateString()}
                    </span>
                    {!review.is_approved && (
                      <span className="text-xs tracking-widest uppercase bg-amber-50 text-amber-600 px-2 py-0.5">
                        Pending
                      </span>
                    )}
                  </div>
                  {review.title && (
                    <p className="text-xs font-medium mb-1">{review.title}</p>
                  )}
                  {review.body && (
                    <p className="text-xs text-secondary leading-relaxed">
                      {review.body}
                    </p>
                  )}
                </div>
                <div className="flex items-center space-x-2 ml-4">
                  {!review.is_approved && (
                    <button
                      onClick={() => handleApprove(review.id, true)}
                      className="p-2 bg-green-50 text-green-600 hover:bg-green-100 transition-colors"
                      title="Approve"
                    >
                      <Check size={14} />
                    </button>
                  )}
                  {review.is_approved && (
                    <button
                      onClick={() => handleApprove(review.id, false)}
                      className="p-2 bg-amber-50 text-amber-600 hover:bg-amber-100 transition-colors"
                      title="Unapprove"
                    >
                      <X size={14} />
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(review.id)}
                    className="p-2 bg-red-50 text-red-500 hover:bg-red-100 transition-colors"
                    title="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ReviewsAdminPage;